// drift-test:no-test-needed: this commit adds overrideSchema?: string to
// ReplaySubCallArgs + passes override_schema in the request body. Pure
// API-shape glue; the openapi drift gate covers the request body shape
// (server-side openapi.yaml now declares override_schema), and the
// backend adapter behavior is covered by claude/codex tests + the
// shim's OverridesExecutor type-assertion path.
//
// traceApi — admin sub-prompt / replay / cross-run endpoints.
//
// Type sourcing: the response/request shapes are aliased from the
// OpenAPI-generated `paths` types in src/api/types.ts. The OpenAPI
// spec at orchestrator/api/openapi.yaml is the single source of
// truth; the hand-written interfaces previously living in this file
// drifted from the wire shape (id vs sub_prompt_id, attempt vs
// attempt_index, uid vs user_id, missing pid) and rendered the
// admin /sub-prompts detail view broken. The aliased approach
// guarantees regenerating types.ts (npm run gen:api) propagates any
// future contract change into the consumer types.
//
// Auth: same convention as `apiClient` — cookie auth (credentials:
// include) + CSRF header. No token plumbing.
//
// Error model:
//  - HTTP 404 with structured `{error: {code, message}}` body → ApiError
//    (the endpoint IS deployed; a specific resource is missing — surface
//    the server's message to the operator).
//  - HTTP 404 with no body / 501 → EndpointUnavailableError (the path
//    isn't wired at all; UI shows "Backend feature pending" placeholder).
//  - HTTP 429       → throw RateLimitError (UI disables button + toasts).
//  - other non-2xx  → throw ApiError with status + body.
//
// Prior to 2026-05-22, ALL 404s mapped to EndpointUnavailableError, which
// silently swallowed legitimate "trace missing for this run", "no such
// product", etc. server-side codes. Operator saw "endpoint not yet
// deployed" when the actual cause was a missing resource on a working
// endpoint. The distinction below restores fidelity.

export class EndpointUnavailableError extends Error {
  readonly endpoint: string;
  constructor(endpoint: string) {
    super(`Backend endpoint not yet deployed: ${endpoint}`);
    this.name = "EndpointUnavailableError";
    this.endpoint = endpoint;
  }
}

export class RateLimitError extends Error {
  readonly retryAfterSec?: number;
  constructor(retryAfterSec?: number) {
    super("Rate limited (try again shortly)");
    this.name = "RateLimitError";
    this.retryAfterSec = retryAfterSec;
  }
}

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL ??
  (typeof window !== "undefined" ? window.location.origin : "");

// Shape the orchestrator returns for any handler-surfaced error
// (errEnvelope helper writes `{"error": {"code": "...", "message": "..."}}`).
// A structured body means "the endpoint ran the handler; here's why it
// 404'd"; an unstructured 404 means the chi router didn't find the path.
type StructuredErrorBody = {
  error?: { code?: string; message?: string };
};

async function adminFetch(path: string, init?: RequestInit): Promise<Response> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      "X-Requested-With": "xhr",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 429) {
    const retryRaw = res.headers.get("retry-after");
    const retryAfterSec = retryRaw ? Number(retryRaw) : undefined;
    throw new RateLimitError(
      Number.isFinite(retryAfterSec) ? retryAfterSec : undefined,
    );
  }

  // For 404 / 501, peek at the body to distinguish "endpoint not wired
  // at all" (raw 404 from the chi router, no JSON body) from "endpoint
  // ran the handler and reported a structured error" (e.g.
  // admin.run_trace_missing for a run whose trace persistence failed).
  // The former is genuinely an EndpointUnavailableError; the latter is
  // an ApiError that the UI can render with the server's actual message.
  if (res.status === 404 || res.status === 501) {
    let body: unknown = null;
    try {
      body = await res.clone().json();
    } catch {
      // not JSON → genuinely-not-wired path
    }
    if (body && typeof body === "object") {
      const errBody = body as StructuredErrorBody;
      if (errBody.error?.code) {
        throw new ApiError(
          res.status,
          body,
          errBody.error.message ?? `HTTP ${res.status} (${errBody.error.code})`,
        );
      }
    }
    throw new EndpointUnavailableError(path);
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      try {
        body = await res.text();
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, body);
  }
  return res;
}

// ── Generated-from-OpenAPI aliases ───────────────────────────────
//
// These types are derived from src/api/types.ts (auto-generated by
// `npm run gen:api` from orchestrator/api/openapi.yaml). Hand-written
// interfaces previously living here drifted from the wire shape and
// broke the admin /sub-prompts detail view; the alias approach keeps
// the consumer types pinned to the OpenAPI contract.
import type { paths } from "./types";

type SubPromptListJSON = NonNullable<
  paths["/api/v1/admin/sub-prompts"]["get"]["responses"]["200"]["content"]["application/json"]
>;
type SubPromptDetailJSON = NonNullable<
  paths["/api/v1/admin/sub-prompts/{sub_prompt_id}"]["get"]["responses"]["200"]["content"]["application/json"]
>;
type ProductSubCallsJSON = NonNullable<
  paths["/api/v1/admin/products/{pid}/sub-calls/{sub_prompt_id}"]["get"]["responses"]["200"]["content"]["application/json"]
>;

// SubPromptSummary — one row in the list endpoint. Field names match
// the wire: id, stage, layer, recent_invocations_24h, success_rate,
// cancel_rate, etc.
export type SubPromptSummary = NonNullable<
  SubPromptListJSON["sub_prompts"]
>[number];

// AllRunsRow / AllProductsRow — one row in the cross-user rollups.
// Aliased from the OpenAPI-generated paths to keep the consumer types
// pinned to the wire contract.
type AllRunsJSON = NonNullable<
  paths["/api/v1/admin/runs"]["get"]["responses"]["200"]["content"]["application/json"]
>;
type AllProductsJSON = NonNullable<
  paths["/api/v1/admin/products"]["get"]["responses"]["200"]["content"]["application/json"]
>;
export type AllRunsRow = NonNullable<AllRunsJSON["runs"]>[number];
export type AllProductsRow = NonNullable<AllProductsJSON["products"]>[number];

// SubPromptDetail — full per-sub-prompt response. Includes prompt
// body, parsed schemas, last N invocations.
export type SubPromptDetail = SubPromptDetailJSON;

// SubCallInvocation — one row in the recent-invocations table on
// the detail page. Fields: run_id, user_id, product_id, item_key,
// attempt (NOT attempt_index), outcome, duration_ms, started_at,
// parsed_output.
export type SubCallInvocation = NonNullable<
  SubPromptDetailJSON["recent_invocations"]
>[number];

// CrossRunSubCallList — cross-run browser response (per-product).
export type CrossRunSubCallList = ProductSubCallsJSON;

export interface ReplaySubCallResponse {
  // The backend does NOT persist — this is the new (transient)
  // output side-by-side with the original.
  sub_prompt_id?: string;
  item_key?: string;
  attempt_index?: number;
  parsed_output?: unknown;
  raw_response?: string;
  prompt_sent?: string;
  duration_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  cost_usd?: number;
  outcome?: string;
  error?: string;
  // attempts[] mirrors AttemptTrace shape so the admin trace panel can
  // render each replay as a "Replay N" tab next to the captured
  // attempts. openapi.yaml declares this as items: {type: object} (too
  // loose for codegen); we type it as unknown[] here and the consumer
  // casts to AttemptTrace at the call site.
  attempts?: unknown[];
  replay_persisted?: boolean;
  override_used?: boolean;
  override_note?: string;
  audit_output_sha256?: string;
}

// ── Endpoints ────────────────────────────────────────────────────

export async function listSubPrompts(): Promise<SubPromptSummary[]> {
  const res = await adminFetch(`/api/v1/admin/sub-prompts`);
  const data = (await res.json()) as
    | { sub_prompts?: SubPromptSummary[] }
    | SubPromptSummary[];
  if (Array.isArray(data)) return data;
  return data.sub_prompts ?? [];
}

export async function getSubPrompt(
  subPromptId: string,
): Promise<SubPromptDetail> {
  const res = await adminFetch(
    `/api/v1/admin/sub-prompts/${encodeURIComponent(subPromptId)}`,
  );
  return (await res.json()) as SubPromptDetail;
}

export async function listProductSubCalls(
  uid: string,
  pid: string,
  subPromptId: string,
  limit = 50,
): Promise<CrossRunSubCallList> {
  // uid is REQUIRED by the backend (LoadRunsForProduct(uid, pid)).
  // Without it the endpoint returns HTTP 400.
  const q = new URLSearchParams({
    uid,
    limit: String(limit),
  });
  const res = await adminFetch(
    `/api/v1/admin/products/${encodeURIComponent(pid)}/sub-calls/${encodeURIComponent(subPromptId)}?${q.toString()}`,
  );
  const data = (await res.json()) as CrossRunSubCallList;
  return { invocations: data.invocations ?? [] };
}

export interface ReplaySubCallArgs {
  uid: string;
  pid: string;
  rid: string;
  subPromptId: string;
  itemKey: string;
  attemptIndex: number;
  // Optional operator override of the prompt template body. Honored
  // by adapters implementing runner.OverridesExecutor (codex + claude
  // both implement it).
  overridePrompt?: string;
  // Optional operator override of the state-packet JSON. Substitutes
  // the captured packet unconditionally — the adapter sees this
  // packet instead of the trace's original.
  overridePacket?: string;
  // Optional operator override of the output JSON schema. Honored by
  // adapters implementing runner.OverridesExecutor (codex + claude
  // both implement it). Empty = use the on-disk schema.
  overrideSchema?: string;
  // Optional engine override ("codex" | "claude"). Empty = replay on the
  // SAME engine the original attempt used (server reads the attempt's
  // adapter_name). Set to deliberately A/B engines from the Customize editor.
  engine?: string;
}

export async function replaySubCall(
  args: ReplaySubCallArgs,
): Promise<ReplaySubCallResponse> {
  const body = JSON.stringify({
    sub_prompt_id: args.subPromptId,
    item_key: args.itemKey,
    attempt_index: args.attemptIndex,
    override_prompt: args.overridePrompt,
    override_packet: args.overridePacket,
    override_schema: args.overrideSchema,
    engine: args.engine,
  });
  const res = await adminFetch(
    `/api/v1/admin/users/${encodeURIComponent(args.uid)}/products/${encodeURIComponent(args.pid)}/runs/${encodeURIComponent(args.rid)}/replay-sub-call`,
    { method: "POST", body },
  );
  return (await res.json()) as ReplaySubCallResponse;
}

// listAllRuns + listAllProducts — cross-user rollups for the "All
// runs" / "All products" admin views. Backend walks every user; v1-
// scale only (no cursor).
export async function listAllRuns(limit = 100): Promise<AllRunsRow[]> {
  const res = await adminFetch(`/api/v1/admin/runs?limit=${limit}`);
  const data = (await res.json()) as AllRunsJSON;
  return data.runs ?? [];
}

export async function listAllProducts(limit = 100): Promise<AllProductsRow[]> {
  const res = await adminFetch(`/api/v1/admin/products?limit=${limit}`);
  const data = (await res.json()) as AllProductsJSON;
  return data.products ?? [];
}

// ── A saved pass (0064): only an admin resumes or closes it ──────
//
// A pass stopped on our side (the usage limit, a rejected engine
// sign-in, a lost hold, the run time limit, a stopped machine) is saved
// and waits for the admin. AllRunsRow carries its `pause_reason`.

type SavedPassJSON = NonNullable<
  paths["/api/v1/admin/users/{uid}/products/{pid}/runs/{rid}/pause"]["get"]["responses"]["200"]["content"]["application/json"]
>;
type PausedRunsJSON = NonNullable<
  paths["/api/v1/admin/runs/paused"]["get"]["responses"]["200"]["content"]["application/json"]
>;
export type SavedPass = SavedPassJSON;
export type PausedRunRow = NonNullable<PausedRunsJSON["runs"]>[number];

function savedPassPath(uid: string, pid: string, rid: string): string {
  return `/api/v1/admin/users/${encodeURIComponent(uid)}/products/${encodeURIComponent(pid)}/runs/${encodeURIComponent(rid)}`;
}

// fetchSavedPass reads what the admin decides on: the stop, what
// changed since it, and what a new run's gate would say now.
export async function fetchSavedPass(
  uid: string,
  pid: string,
  rid: string,
): Promise<SavedPass> {
  const res = await adminFetch(`${savedPassPath(uid, pid, rid)}/pause`);
  return (await res.json()) as SavedPass;
}

// resumeRun resumes the saved pass (202). It sends no body: the pass
// runs from its stored request.
export async function resumeRun(uid: string, pid: string, rid: string): Promise<void> {
  await adminFetch(`${savedPassPath(uid, pid, rid)}/resume`, { method: "POST" });
}

// closeRun closes the saved pass and refunds its credit (204).
export async function closeRun(uid: string, pid: string, rid: string): Promise<void> {
  await adminFetch(`${savedPassPath(uid, pid, rid)}/close`, { method: "POST" });
}

// listPausedRuns lists every saved pass, soonest closing first.
export async function listPausedRuns(): Promise<PausedRunRow[]> {
  const res = await adminFetch(`/api/v1/admin/runs/paused`);
  const data = (await res.json()) as PausedRunsJSON;
  return data.runs ?? [];
}
